import React, { useEffect, useRef, useState, useCallback } from 'react';
import { colors } from '../../constants/theme';

interface ResizableSplitProps {
  children: [React.ReactNode, React.ReactNode];
  defaultLeftWidth?: number;
  minLeftWidth?: number;
  maxLeftWidth?: number;
  gap?: number;
}

export const ResizableSplit: React.FC<ResizableSplitProps> = ({
  children,
  defaultLeftWidth = 400,
  minLeftWidth = 300,
  maxLeftWidth = 800,
  gap = 12,
}) => {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;
    startWidthRef.current = leftWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [leftWidth]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !leftPanelRef.current || !handleRef.current) return;
    
    const delta = e.clientX - startXRef.current;
    const newWidth = Math.max(minLeftWidth, Math.min(maxLeftWidth, startWidthRef.current + delta));
    
    // Direct DOM update - no React re-render during drag
    leftPanelRef.current.style.width = `${newWidth}px`;
    handleRef.current.style.left = `${newWidth + gap / 2 - 6}px`;
  }, [isDragging, minLeftWidth, maxLeftWidth, gap]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging || !leftPanelRef.current) return;
    
    // Update state with final width
    const finalWidth = leftPanelRef.current.offsetWidth;
    setLeftWidth(finalWidth);
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isDragging]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Initialize handle position
  useEffect(() => {
    if (handleRef.current) {
      handleRef.current.style.left = `${leftWidth + gap / 2 - 6}px`;
    }
  }, [leftWidth, gap]);

  return (
    <div style={{ display: 'flex', position: 'relative', height: '100%', width: '100%', gap: gap }}>
      <div
        ref={leftPanelRef}
        style={{ width: leftWidth, height: '100%', flexShrink: 0, overflow: 'hidden' }}
      >
        {children[0]}
      </div>

      <div
        ref={handleRef}
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          left: leftWidth + gap / 2 - 6,
          width: 12,
          height: '100%',
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: 'none',
        }}
      >
        <div style={{
          width: 2,
          height: '100%',
          backgroundColor: isDragging ? colors.accent : 'transparent',
          borderRadius: 1,
          transition: isDragging ? 'none' : 'background-color 0.2s ease',
        }} />
      </div>

      <div style={{ flex: 1, height: '100%', overflow: 'hidden', minWidth: 0 }}>
        {children[1]}
      </div>
    </div>
  );
};
